import { useState } from 'react';

import { cn } from 'lib/helpers/utility';

import Button from 'components/shared/Button/Button';
import Icon from 'components/shared/Icon/Icon';

import introImage from 'assets/Images/myriad-motion/myriad-motion-intro.png';

import './IntroStep.scss';

const ListItem = ({ icon, color, children }) => (
  <div className="flex items-start gap-2">
    <Icon icon={icon} color={color} size={18} className="mt-0.5 flex-shrink-0" />
    <span className="text-sm font-400 text-neutral-800">{children}</span>
  </div>
);

const WHY_YOU_ARE_HERE_ITEMS = [
  {
    icon: 'guarantee-mono',
    color: 'warning',
    text: 'Our pricing model changed how you pay for Practice Management'
  },
  {
    icon: 'myscribeai-mono',
    color: 'warning',
    text: 'Our system will change how you run it'
  },
  {
    icon: 'star-1-mono',
    color: 'warning',
    text: 'And now, our onboarding will change how you implement it'
  }
];

const WHAT_MYRIAD_MOTION_DOES_ITEMS = [
  'Places control firmly in your hands',
  'Revolutionizes onboarding with full visibility and choice',
  'Transforms EHR setup from compliance-driven to practice-driven',
  'Automates what slows you down—without sacrificing precision',
  'Aligns workflows with your practice philosophy, not ours. You will customize every step to your practice'
];

const IntroStep = ({ onNext }) => {
  const [isExiting, setIsExiting] = useState(false);

  const handleStartJourney = () => {
    setIsExiting(true);
    // Wait for exit animation to complete before transitioning
    setTimeout(() => {
      onNext();
    }, 500); // Match animation duration
  };

  return (
    <div className="flex h-full w-full overflow-hidden bg-white">
      <div className={cn('intro-left-column flex w-1/2 flex-col', isExiting && 'exiting')}>
        <div className="w-full">
          <img src={introImage} alt="Myriad Motion" className="h-auto w-full object-cover" />
        </div>
        <div className="flex flex-1 flex-col justify-center px-16">
          <h2 className="mb-[30px] text-3xl font-500 text-primary-900">
            You're not just beginning onboarding.
            <br />
            You're entering a movement.
          </h2>
          <p className="text-base font-400 text-neutral-700">
            Myriad Health was built to restore control to independent providers - to protect the
            choices you make for your practice, to give you tools normally reserved for large health
            systems, and to eliminate the administrative burden that pulls you away from care.
          </p>
        </div>
      </div>

      <div
        className={cn(
          'w-px bg-neutral-300',
          isExiting && 'opacity-0 transition-opacity duration-300'
        )}
      />

      <div className={cn('intro-right-column flex w-1/2 flex-col', isExiting && 'exiting')}>
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-white px-16 pb-4 pt-10 border-b-neutral-200 border-x-0 border-t-0 border border-solid">
          <div className="mb-2 flex items-center gap-2">
            <Icon
              icon="wizard-white"
              size={24}
              color="primary"
              className="motion-onboarding-icon"
            />
            <h1 className="text-2xl font-500 text-neutral-900">Welcome to Myriad Motion</h1>
          </div>
          <p className="text-sm font-400 text-neutral-700">
            Your onboarding journey starts here. Let's set up your practice the way you want it.
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-16">
          <div className="mb-6 rounded-xl border border-solid border-orange-dark-200 bg-orange-dark-50 p-5">
            <h3 className="mb-4 text-lg font-500 text-neutral-900">Why you're here</h3>
            <div className="flex flex-col gap-3">
              {WHY_YOU_ARE_HERE_ITEMS.map(({ icon, color, text }) => (
                <ListItem key={icon} icon={icon} color={color}>
                  {text}
                </ListItem>
              ))}
            </div>
          </div>

          <div className="mb-6 p-5">
            <h3 className="mb-4 text-lg font-500 text-neutral-900">What Myriad Motion does</h3>
            <div className="flex flex-col gap-3">
              {WHAT_MYRIAD_MOTION_DOES_ITEMS.map((text, index) => (
                <ListItem key={index} icon="checkbox-circle-mono" color="primary">
                  {text}
                </ListItem>
              ))}
            </div>
          </div>

          <div className="mb-6 rounded-xl border border-primary-100 bg-primary-50 p-5">
            <div className="mb-3 flex items-center gap-2">
              <div className="flex items-center justify-center rounded-md bg-white p-1">
                <Icon icon="medal-dollar-mono" color="primary" size={20} />
              </div>
              <h3 className="text-lg font-500 text-neutral-900">Our promise</h3>
            </div>
            <p className="mb-3 text-sm text-neutral-600">
              We're here to disrupt healthcare IT-not your practice. We build systems that work the
              way your practice works, so you can spend more time practicing medicine and less time
              managing software. Let's begin.
            </p>
            <p className="text-2xl font-600 text-primary-900">
              "This is not onboarding. This is empowerment."
            </p>
          </div>
        </div>

        {/* Sticky Footer with Button */}
        <div className="sticky bottom-0 z-10 bg-white px-16 pb-6 pt-4 border-t-neutral-200 border-x-0 border-b-0 border-solid border">
          <Button
            text="Start my journey today"
            onClick={handleStartJourney}
            className="w-full"
            disabled={isExiting}
          />
        </div>
      </div>
    </div>
  );
};

export default IntroStep;
